// ⚙️ 개인 설정 기기 간 동기화 (2026-09-07 사용자 "개인설정을 데탑/앱/웹이 동일하게 해도 되는 것들은 서로 동기화")
//
//   구조: 설정 값 자체는 종전대로 localStorage(각 setter)가 정본처럼 즉시 반영하고, 여기서는 **계정 단위 서버 사본**
//   (/api/tpt/user-prefs, server/routes/userPrefs.ts)과 키별 '변경 시각(at)' 으로 맞춘다 — 최근 변경 우선(LWW).
//   병합 규칙은 prefsMerge.ts(순수 함수, scripts/check-prefs-sync.mjs 회귀) — 이 파일은 저장소·네트워크·이벤트만 담당.
//   · setter → prefs.markPrefChanged(key) → (훅) onPrefChanged: 이 기기의 at 을 찍고 디바운스 push.
//   · 로그인 직후·창 복귀(visibilitychange)·설정 모달 열 때 pull → 서버 at 이 더 새로운 키만 로컬에 반영.
//     반영은 **기존 setter 를 그대로 호출**해 화면 갱신 이벤트(테마 적용·칩 표시 등)가 종전 경로로 나간다.
//     반영 중엔 applying 플래그로 onPrefChanged 를 막아 되밀기(핑퐁)를 차단.
//   · 동기화 대상은 SYNCED_PREFS 만 — 화면 배치·배율·다운로드 폴더·자동 시작 같은 **기기 특성은 넣지 않는다**
//     (서버 SYNC_KEYS 화이트리스트와 같은 집합, 한쪽만 늘리면 무시된다).
import { useAuthStore } from '../stores/authStore';
import { diagLog } from '../desktop/diag';
import { applyTheme, normalizeTheme, paintTheme, THEME_KEY, THEME_DEFAULT } from './theme';
import {
	setPrefChangeHook, setSendKey, setMentionSuffix, setMentionRankNim, setNotifPrefs, resetNotifPrefs, setShowLastMessage, setThreadMediaVisible,
	setComposerChipsVisible, setNavUnreadBadge, setAppBadge, setVocFabVisible, setNameLang2, setRoomGap, resetRoomGap, type NotifPrefs, type RoomGap,
} from './prefs';
import { writeRightTabsPref, resetRightTabsPref, RIGHT_TABS_KEY, type RightTabsPref } from './rightPanelTabs';
import { planReconcile, alignAtAfterPush, type SyncedPref, type Entry } from './prefsMerge';
import { isTauri } from '../desktop/env';
import { isNativeApp } from '../utils/platform';

// 🔔 알림 상세(tpt-notif-prefs)는 **플랫폼 부류별로 따로** 동기화한다 (2026-09-16 사용자 "알림기능에 한하여 데탑/앱은 분리").
//   로컬 키는 종전 그대로 'tpt-notif-prefs' 하나 — 서버 문서에서만 'tpt-notif-prefs-desktop' / '-app' / '-web' 으로 갈라 둔다.
//   같은 부류 기기끼리(데탑↔데탑, 폰↔태블릿)는 여전히 맞춰지고, 데탑↔앱↔웹 사이는 서로 건드리지 않는다.
//   🔴 서버 SYNC_KEYS 에는 세 서버 키가 들어 있어야 한다(scripts/check-prefs-sync.mjs 가 이 매핑을 알고 비교).
export type PrefsPlatform = 'desktop' | 'app' | 'web';
export const NOTIF_PREFS_LOCAL_KEY = 'tpt-notif-prefs';
export const NOTIF_PREFS_SERVER_KEYS = ['tpt-notif-prefs-desktop', 'tpt-notif-prefs-app', 'tpt-notif-prefs-web'] as const;
/** 이 기기의 플랫폼 부류 — utils/platform.applyPlatformAttr 와 같은 판정(html[data-platform]) */
export function prefsPlatform(): PrefsPlatform { return isTauri() ? 'desktop' : isNativeApp() ? 'app' : 'web'; }
function notifServerKey(): string { return `${NOTIF_PREFS_LOCAL_KEY}-${prefsPlatform()}`; }
/** 서버 문서 → 로컬 키 공간: 내 부류의 알림 키만 'tpt-notif-prefs' 로 받고, 다른 부류의 알림 키는 버린다. */
function fromServer(doc: Record<string, Entry>): Record<string, Entry> {
	const mine = notifServerKey();
	const out: Record<string, Entry> = {};
	for (const [k, e] of Object.entries(doc)) {
		if (k === mine) out[NOTIF_PREFS_LOCAL_KEY] = e;
		else if (k.startsWith(NOTIF_PREFS_LOCAL_KEY)) continue;   // 다른 부류(또는 구 공용 키) — 이 기기와 무관
		else out[k] = e;
	}
	return out;
}
/** 로컬 키 공간 → 서버 문서: 'tpt-notif-prefs' 는 내 부류의 서버 키로 올린다. */
function toServer(prefs: Record<string, Entry>): Record<string, Entry> {
	const out: Record<string, Entry> = {};
	for (const [k, e] of Object.entries(prefs)) out[k === NOTIF_PREFS_LOCAL_KEY ? notifServerKey() : k] = e;
	return out;
}

/** 이 기기에서 사용자가 각 키를 바꾼 시각 {key: ms} — user-scoped(sessionPurge 등록): 다음 사용자에게 '더 새로운 로컬' 로 남지 않게. */
const AT_KEY = 'tpt-prefs-at';
/** 마지막으로 동기화한 계정(username) — KEEP(로그아웃에도 남음). 다른 사용자가 로그인하면 첫 pull 을 adopt 모드로 돌려
 *  이 기기에 남은 이전 사용자 값(테마 등)을 새 계정에 seed 하지 않는다 (2026-09-07 리뷰 🔴). */
const OWNER_KEY = 'tpt-prefs-owner';
/** 서버값이 로컬에 반영됐을 때 — detail.keys 가 바뀐 키 목록. 열려 있는 설정 모달이 상태를 다시 읽는다. */
export const PREFS_SYNCED_EVENT = 'tpt:prefs-synced';
const PULL_MIN_GAP_MS = 60_000;   // 창 복귀 때마다 치지 않게
const PUSH_DEBOUNCE_MS = 700;

// defaults = "설정 안 함" 과 같은 뜻인 저장값들(첫 동기화 때 서버에 심지 않는다). null = 키 없음.
export const SYNCED_PREFS: SyncedPref[] = [
	{ key: THEME_KEY, defaults: [THEME_DEFAULT, null] },
	{ key: 'tpt-send-key', defaults: ['enter', null] },
	{ key: 'tpt-mention-suffix', defaults: ['', null] },
	{ key: 'tpt-mention-rank-nim', defaults: ['0', null] },
	{ key: NOTIF_PREFS_LOCAL_KEY, defaults: [null] },   // 🔔 서버에선 플랫폼 부류별 키(fromServer/toServer)
	{ key: 'tpt-show-last-message', defaults: ['0', null] },
	{ key: 'tpt-thread-media', defaults: ['1', null] },   // 🖼 댓글 사진·영상 표시 (2026-10-22, 기본 표시)
	{ key: 'tpt-composer-chips', defaults: ['1', null] },
	{ key: 'tpt-nav-unread-badge', defaults: ['0', null] },
	{ key: 'tpt-app-badge', defaults: ['1', null] },   // 🔢 앱 아이콘 숫자 배지 (2026-09-18)
	{ key: 'tpt-voc-fab', defaults: ['1', null] },
	{ key: 'tpt-name-lang2', defaults: ['', null] },   // 🌐 제2 이름 표기 언어 (미등록='')
	{ key: 'tpt-room-gap', defaults: [null] },   // 📏 대화 목록 줄 간격 (모바일/데스크톱 JSON — 기본은 저장하지 않으므로 키 없음이 곧 기본)
	{ key: RIGHT_TABS_KEY, defaults: [null] },
	// 📅 일정 화면 체크 상태 3종 (2026-09-20 사용자 "위젯이 일정의 선택된 항목과 동일하게") — 서버 사본을 위젯(core/widgetSummary)이 읽는다.
	//   기본값은 화면의 초기값 문자열 그대로(화면이 마운트마다 그 값을 localStorage 에 쓰므로 키가 늘 있다).
	{ key: 'tpt-cal-scopes', defaults: ['{"personal":true,"dept":true,"shared":true,"company":true,"project":true}', null] },
	{ key: 'tpt-cal-shown-pjts', defaults: ['[]', null] },
	{ key: 'tpt-cal-countries', defaults: ['["KR","VN","CN","MX","JP"]', '["KR","VN","CN","MX"]', null] },   // JP 추가(2026-09-28) — 옛 기본값도 '설정 안 함' 으로 본다
];
const KEYS = new Set(SYNCED_PREFS.map((p) => p.key));

let applying = false;
const dirty = new Set<string>();
let pushTimer: number | null = null;
let lastPullAt = 0;
let installed = false;

function get(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function localValues(): Record<string, string | null> { const out: Record<string, string | null> = {}; for (const k of KEYS) out[k] = get(k); return out; }
function readAt(): Record<string, number> {
	try { const m = JSON.parse(localStorage.getItem(AT_KEY) || '{}') as Record<string, unknown>; const out: Record<string, number> = {}; for (const [k, v] of Object.entries(m)) { const n = Number(v); if (n > 0) out[k] = n; } return out; } catch { return {}; }
}
function writeAt(m: Record<string, number>): void { try { localStorage.setItem(AT_KEY, JSON.stringify(m)); } catch { /* private mode */ } }
function loggedIn(): boolean { const { authToken, userId } = useAuthStore.getState(); return !!authToken && !!userId; }
function readOwner(): string { try { return localStorage.getItem(OWNER_KEY) || ''; } catch { return ''; } }
function writeOwner(u: string): void { try { localStorage.setItem(OWNER_KEY, u); } catch { /* private mode */ } }

/** 로그아웃(purgeSession) 때 — 디바운스 대기 중인 push·pull 스로틀을 버린다. 이전 사용자의 변경이 다음 사용자 토큰으로 나가지 않게. */
export function resetPrefsSync(): void {
	if (pushTimer !== null) { window.clearTimeout(pushTimer); pushTimer = null; }
	dirty.clear();
	lastPullAt = 0;
}

/** prefs.markPrefChanged 의 실체(installPrefsSync 가 훅으로 등록) — 이 기기의 변경 시각을 찍고 서버에 밀어 올린다(디바운스). 서버값 반영 중이면 무시. */
function onPrefChanged(key: string): void {
	if (applying || !KEYS.has(key)) return;
	const at = readAt(); at[key] = Date.now(); writeAt(at);
	dirty.add(key);
	if (pushTimer !== null) window.clearTimeout(pushTimer);
	pushTimer = window.setTimeout(() => { pushTimer = null; void flushPush(); }, PUSH_DEBOUNCE_MS);
}

async function post(prefs: Record<string, Entry>): Promise<void> {
	if (!Object.keys(prefs).length || !loggedIn()) return;
	try {
		const r = await fetch('/api/tpt/user-prefs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prefs: toServer(prefs) }) });
		if (!r.ok) throw new Error(`user-prefs ${r.status}`);
		const j = await r.json() as { prefs?: Record<string, Entry> };
		const server = fromServer(j.prefs || {});
		writeAt(alignAtAfterPush(prefs, server, localValues(), readAt()));
		// 서버가 더 새로운 키(다른 기기가 방금 바꿈)가 있으면 그 값으로 맞춘다 — 되밀지는 않는다.
		reconcile(server, false, 'push');
	} catch (e) {
		diagLog('prefs-sync push fail', String((e as Error).message || e));   // 실패 — 다음 pull 때 로컬 at 이 더 새로우면 다시 올라간다
	}
}

async function flushPush(): Promise<void> {
	if (!dirty.size) return;
	const at = readAt();
	const prefs: Record<string, Entry> = {};
	for (const k of dirty) prefs[k] = { v: get(k) ?? '', at: at[k] || Date.now() };
	dirty.clear();
	await post(prefs);
}

/** 서버 문서와 로컬을 키별 at 로 맞춘다. pushBack=true(pull) 면 로컬이 더 새로운 키·첫 동기화 비기본값을 서버에 올린다.
 *  adopt=true(계정이 바뀐 기기의 첫 pull) 면 seed 대신 서버에 없는 키를 기본값으로 되돌린다. */
function reconcile(server: Record<string, Entry>, pushBack: boolean, reason: string, adopt = false): void {
	const plan = planReconcile({ synced: SYNCED_PREFS, server, local: localValues(), localAt: readAt(), pushBack, now: Date.now(), adopt });
	for (const a of plan.apply) applyLocal(a.key, a.v);
	writeAt(plan.at);
	if (plan.apply.length) {
		const keys = plan.apply.map((a) => a.key);
		diagLog('prefs-sync applied', `by=${reason}`, keys.join(','));
		try { window.dispatchEvent(new CustomEvent(PREFS_SYNCED_EVENT, { detail: { keys, reason } })); } catch { /* */ }
	}
	if (Object.keys(plan.push).length) void post(plan.push);
}

// 서버값을 로컬에 반영 — 각 키의 기존 setter 를 부른다(화면 갱신 이벤트 재사용). 파싱 실패는 원문 그대로 저장.
function applyLocal(key: string, v: string): void {
	applying = true;
	try {
		switch (key) {
			case THEME_KEY: applyTheme(normalizeTheme(v), 'sync'); break;
			case 'tpt-send-key': setSendKey(v === 'shift-enter' ? 'shift-enter' : 'enter'); break;
			case 'tpt-mention-suffix': setMentionSuffix(v); break;
			case 'tpt-mention-rank-nim': setMentionRankNim(v === '1'); break;
			case 'tpt-notif-prefs': if (v) setNotifPrefs(JSON.parse(v) as Partial<NotifPrefs>); else resetNotifPrefs(); break;
			case 'tpt-show-last-message': setShowLastMessage(v === '1'); break;
			case 'tpt-thread-media': setThreadMediaVisible(v !== '0'); break;
			case 'tpt-composer-chips': setComposerChipsVisible(v !== '0'); break;
			case 'tpt-nav-unread-badge': setNavUnreadBadge(v === '1'); break;
			case 'tpt-app-badge': setAppBadge(v !== '0'); break;
			case 'tpt-voc-fab': setVocFabVisible(v !== '0'); break;
			case 'tpt-name-lang2': setNameLang2((v || '') as Parameters<typeof setNameLang2>[0]); break;
			case 'tpt-room-gap': if (v) setRoomGap(JSON.parse(v) as Partial<RoomGap>); else resetRoomGap(); break;
			case RIGHT_TABS_KEY: if (v) writeRightTabsPref(JSON.parse(v) as RightTabsPref); else resetRightTabsPref(); break;
			// 📅 일정 필터 — 값만 놓아두면 된다. 열려 있는 일정 화면은 PREFS_SYNCED_EVENT(detail.keys)를 듣고 다시 읽는다.
			case 'tpt-cal-scopes': case 'tpt-cal-shown-pjts': case 'tpt-cal-countries':
				if (v) localStorage.setItem(key, v); else localStorage.removeItem(key); break;
			default: localStorage.setItem(key, v);
		}
	} catch {
		try { if (v) localStorage.setItem(key, v); else localStorage.removeItem(key); } catch { /* private mode */ }
	} finally { applying = false; }
}

/** 서버 사본을 받아 로컬과 맞춘다. force=false 면 60초 안 재호출은 건너뛴다(창 복귀 연타 방지). */
export async function pullSyncedPrefs(reason: string, force = false): Promise<void> {
	if (!loggedIn()) return;
	if (!force && Date.now() - lastPullAt < PULL_MIN_GAP_MS) return;
	lastPullAt = Date.now();
	try {
		const r = await fetch('/api/tpt/user-prefs');
		if (!r.ok) return;   // 구 사이드카(404)·일시 장애 — 로컬 유지
		const j = await r.json() as { prefs?: Record<string, Entry> };
		const me = useAuthStore.getState().username || '';
		const owner = readOwner();
		const adopt = !!owner && !!me && owner !== me;   // 이 기기의 마지막 동기화 계정과 다른 사용자 → 이 기기 값을 새 계정에 심지 않는다
		reconcile(fromServer(j.prefs || {}), !adopt, reason, adopt);
		if (me && owner !== me) writeOwner(me);
	} catch { /* 오프라인 — 로컬 유지 */ }
}

/** main.tsx 에서 1회 — 변경 훅 등록·로그인 감지·창 복귀 pull + 같은 오리진 다른 창(팝아웃)의 테마 변경 즉시 반영. */
export function installPrefsSync(): void {
	if (installed || typeof window === 'undefined') return;
	installed = true;
	setPrefChangeHook(onPrefChanged);
	// 로그인 완료(토큰 생김) 순간 pull — 부팅 시 이미 로그인 상태면 즉시.
	let prevToken = useAuthStore.getState().authToken;
	if (prevToken) void pullSyncedPrefs('boot', true);
	useAuthStore.subscribe((s) => {
		if (s.authToken && s.authToken !== prevToken) { lastPullAt = 0; void pullSyncedPrefs('login', true); }
		prevToken = s.authToken;
	});
	document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void pullSyncedPrefs('visible'); });
	// 🪟 데탑 팝아웃·웹 다른 탭이 테마를 바꾸면 이 문서도 따라온다 — 저장은 하지 않는다(창 간 핑퐁 방지).
	window.addEventListener('storage', (e) => {
		if (e.key === THEME_KEY && e.newValue) paintTheme(normalizeTheme(e.newValue), 'storage');
	});
}
